import { Divider } from '@outdoorsyco/bonfire';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Text from '@/components/switchback/Text/Text';

import GuestDetailItem from './GuestDetailItem';

interface IGuestsMinMaxValues {
  adults: { min: number; max: number };
  children: { min?: number; max: number };
  infants: { min?: number; max: number };
}

const DEFAULT_GUESTS_MIN_MAX_VALUES: IGuestsMinMaxValues = {
  adults: { min: 1, max: 10 },
  children: { min: 0, max: 10 },
  infants: { min: 0, max: 10 },
};

interface IGuestDetailProps {
  adultsValue: number;
  childrenValue: number;
  infantsValue: number;
  petsValue: number;
  onChangeAdults: (value: number) => void;
  onChangeChildren: (value: number) => void;
  onChangeInfants: (value: number) => void;
  onChangePets: (value: number) => void;
  isGuestOccupancyAvailable?: boolean;
  isPetOccupancyAvailable?: boolean;
  guestOccupancyMinMaxValues?: IGuestsMinMaxValues;
  maxPets: number;
}

const GuestDetail: React.FC<IGuestDetailProps> = ({
  adultsValue,
  childrenValue,
  infantsValue,
  petsValue,
  onChangeAdults,
  onChangeChildren,
  onChangeInfants,
  onChangePets,
  isGuestOccupancyAvailable,
  isPetOccupancyAvailable = false,
  guestOccupancyMinMaxValues = DEFAULT_GUESTS_MIN_MAX_VALUES,
  maxPets = 4,
}) => {
  const intl = useIntl();
  const labels = {
    adults: {
      label: intl.formatMessage({
        description: 'Guests filter > Adult QuantitySelector title',
        defaultMessage: 'Adults',
        id: 'og/mwU',
      }),
      disclaimer: intl.formatMessage({
        description: 'Guests filter > Adult QuantitySelector disclaimer',
        defaultMessage: 'Ages 13 or above',
        id: 'Chp+P6',
      }),
    },
    children: {
      label: intl.formatMessage({
        description: 'Guests filter > Children QuantitySelector title',
        defaultMessage: 'Children',
        id: 'f7swIl',
      }),
      disclaimer: isGuestOccupancyAvailable
        ? intl.formatMessage({
            defaultMessage: 'Ages 3-12',
            id: 'cn4PHX',
          })
        : intl.formatMessage({
            description: 'Guests filter > Children QuantitySelector disclaimer',
            defaultMessage: 'Ages 12 or under',
            id: 'NouNP2',
          }),
    },
    infants: {
      label: intl.formatMessage({
        description: 'Guests filter > Infants QuantitySelector title',
        defaultMessage: 'Infants',
        id: '4sPQXv',
      }),
      disclaimer: intl.formatMessage({
        description: 'Guests filter > Infants QuantitySelector disclaimer',
        defaultMessage: 'Ages 2 or under',
        id: '0ipV9p',
      }),
    },
    pets: {
      label: intl.formatMessage({
        description: 'Guests filter > Pets QuantitySelector title',
        defaultMessage: 'Pets',
        id: 'GnbM8z',
      }),
      disclaimer: intl.formatMessage({
        description: 'Guests filter > Pets QuantitySelector disclaimer',
        defaultMessage: 'Bring your furry friend',
        id: 'bTCBWr',
      }),
    },
  };

  return (
    <div className="grow">
      {!isGuestOccupancyAvailable && (
        <Text
          type="paragraph"
          className="mb-5 text-xs font-bold text-gray-500 uppercase semiHighlight">
          <FormattedMessage defaultMessage="guests" id="83vj7t" />
        </Text>
      )}
      <GuestDetailItem
        label={labels.adults.label}
        guestsCount={adultsValue}
        onChange={onChangeAdults}
        disclaimer={labels.adults.disclaimer}
        minQuantity={guestOccupancyMinMaxValues.adults.min}
        maxQuantity={guestOccupancyMinMaxValues.adults.max}
      />
      <Divider className="my-4" />
      <GuestDetailItem
        label={labels.children.label}
        guestsCount={childrenValue}
        onChange={onChangeChildren}
        disclaimer={labels.children.disclaimer}
        maxQuantity={guestOccupancyMinMaxValues.children.max}
      />
      {isGuestOccupancyAvailable && (
        <>
          <Divider className="my-4" />
          <GuestDetailItem
            label={labels.infants.label}
            guestsCount={infantsValue}
            onChange={onChangeInfants}
            disclaimer={labels.infants.disclaimer}
            maxQuantity={guestOccupancyMinMaxValues.infants.max}
          />
        </>
      )}
      {isPetOccupancyAvailable && (
        <>
          <Divider className="my-4" />
          <GuestDetailItem
            label={labels.pets.label}
            guestsCount={petsValue}
            onChange={onChangePets}
            disclaimer={labels.pets.disclaimer}
            maxQuantity={maxPets}
          />
        </>
      )}
    </div>
  );
};

export default GuestDetail;
